import { ReactNode, useState } from "react"
import { createPortal } from "react-dom"
import classNames from "classnames"
import { ClickAwayListenerV2 } from "@nova/component-click-away-listener"
import { EmojiPicker } from "./EmojiPicker"

const pickerHeight = 435
const gap = 8

type Anchor = { top?: number; bottom?: number; right: number }

export const EmojiPickerDropdown = ({
    onSelect,
    onOpenChange,
    className,
    children,
}: {
    onSelect: (emoji: string) => void
    onOpenChange?: (isOpen: boolean) => void
    className?: string
    children: ReactNode
}) => {
    const [anchor, setAnchor] = useState<Anchor>()

    const setOpen = (next: Anchor | undefined) => {
        setAnchor(next)
        onOpenChange?.(!!next)
    }

    const toggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        if (anchor) return setOpen(undefined)

        const rect = event.currentTarget.getBoundingClientRect()
        const right = window.innerWidth - rect.right
        const fitsBelow = rect.bottom + gap + pickerHeight <= window.innerHeight
        setOpen(
            fitsBelow
                ? { top: rect.bottom + gap, right }
                : { bottom: window.innerHeight - rect.top + gap, right }
        )
    }

    return (
        <>
            <button
                type="button"
                onClick={toggle}
                className={classNames("flex cursor-pointer items-center justify-center", className)}
            >
                {children}
            </button>
            {anchor &&
                createPortal(
                    <ClickAwayListenerV2
                        onClickAway={() => setOpen(undefined)}
                        onClick={(e) => e.stopPropagation()}
                        className="fixed z-[9999] overflow-hidden rounded-xl border border-white-200 shadow-xl shadow-black-900/50"
                        style={{ top: anchor.top, bottom: anchor.bottom, right: anchor.right }}
                    >
                        <EmojiPicker
                            onSelect={(emoji) => {
                                onSelect(emoji)
                                setOpen(undefined)
                            }}
                        />
                    </ClickAwayListenerV2>,
                    document.getElementById("portal") ?? document.body
                )}
        </>
    )
}
